import { FC } from 'react'
import styles from './subscriptionCard.module.scss'

export interface PricingFeature {
  included: boolean
  text: string
}

export interface PricingAddon {
  description: string
  name: string
  price: string
}

export interface SubscriptionCardData {
  addon?: PricingAddon
  ctaText: string
  defaultFeatures: PricingFeature[]
  description: string
  frequency: string
  id: string
  optionalFeatures?: PricingFeature[]
  originalPrice?: number
  period: string
  price: number
  priceId?: string
  savingsBadge?: string
  savingsBadgeType?: 'green' | 'red'
  title: string
}

interface SubscriptionCardProps {
  cancelDate?: string | null
  data: SubscriptionCardData
  isActivePlan?: boolean
  isCanceling?: boolean
  isMutatingPlan?: boolean
  isProcessing?: boolean
  isPromoApplied?: boolean
  isSelected?: boolean
  onCancel?: () => void
  onClick?: () => void
  onGetPlan?: () => void
  onResume?: () => void
}

const PROMO_DISCOUNT = 0.2 // 20% discount

const SubscriptionCard: FC<SubscriptionCardProps> = ({
  cancelDate = null,
  data,
  isActivePlan = false,
  isCanceling = false,
  isMutatingPlan = false,
  isProcessing = false,
  isPromoApplied = false,
  isSelected = false,
  onCancel,
  onClick,
  onGetPlan,
  onResume,
}) => {
  const discountedPrice = isPromoApplied
    ? (data.price * (1 - PROMO_DISCOUNT)).toFixed(2)
    : null

  return (
    <div
      className={`${styles.card} ${isSelected ? styles.selected : ''} ${isActivePlan ? styles.activePlan : ''}`}
      onClick={onClick}
    >
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h3 className={styles.title}>{data.title}</h3>
          {isCanceling && (
            <span className={styles.cancelingBadge}>
              Canceling{cancelDate ? ` · ends ${cancelDate}` : ''}
            </span>
          )}
          {data.savingsBadge && !isPromoApplied && (
            <span
              className={`${styles.savingsBadge} ${data.savingsBadgeType === 'red' ? styles.savingsBadgeRed : ''}`}
            >
              {data.savingsBadge}
            </span>
          )}
          {isPromoApplied && (
            <span className={styles.promoBadge}>Promo Active</span>
          )}
        </div>
        <p className={styles.description}>{data.description}</p>
      </div>

      <div className={styles.priceSection}>
        <div className={styles.priceRow}>
          {isPromoApplied ? (
            <>
              <span className={styles.priceAmount}>${discountedPrice}</span>
              <span className={styles.pricePeriod}>/ {data.period}</span>
              <span className={styles.originalPrice}>${data.price}.00</span>
            </>
          ) : (
            <>
              <span className={styles.priceAmount}>${data.price}.00</span>
              <span className={styles.pricePeriod}>/ {data.period}</span>
            </>
          )}
        </div>
        <p className={styles.frequency}>{data.frequency}</p>
      </div>

      <div className={styles.divider} />

      <div className={styles.featuresSection}>
        <h4 className={styles.featuresTitle}>Default Features</h4>
        <ul className={styles.featuresList}>
          {data.defaultFeatures.map((feature, index) => (
            <li className={styles.featureItem} key={index}>
              <span className={styles.featureCheck} />
              <span className={styles.featureText}>{feature.text}</span>
            </li>
          ))}
        </ul>
      </div>

      {data.optionalFeatures && data.optionalFeatures.length > 0 && (
        <div className={styles.optionalSection}>
          <h4 className={styles.featuresTitle}>Optional Advanced Features</h4>
          <ul className={styles.featuresList}>
            {data.optionalFeatures.map((feature, index) => (
              <li
                className={`${styles.featureItem} ${styles.optional}`}
                key={index}
              >
                <span className={styles.featureStar}>
                  <svg
                    fill="none"
                    height="13"
                    viewBox="0 0 11 10"
                    width="14"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.93867 8.17867L5.03867 6.912L7.13867 8.19533L6.58867 5.79533L8.43867 4.19533L6.00533 3.97867L5.03867 1.712L4.072 3.962L1.63867 4.17867L3.48867 5.79533L2.93867 8.17867ZM1.922 9.57733L2.74867 6.03533L0 3.654L3.62067 3.34067L5.03867 0L6.45667 3.34L10.0767 3.65333L7.328 6.03467L8.15533 9.57667L5.03867 7.69667L1.922 9.57733Z"
                      fill={isActivePlan ? '#FFFFFF' : '#73E67A'}
                    />
                  </svg>
                </span>
                <span className={styles.featureText}>{feature.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isActivePlan ? (
        <div className={styles.activeButtonsContainer}>
          {isCanceling ? (
            <button
              className={styles.resumeButton}
              disabled={isMutatingPlan}
              onClick={(e) => {
                e.stopPropagation()
                onResume?.()
              }}
              type="button"
            >
              {isMutatingPlan ? 'Resuming…' : 'Resume plan'}
            </button>
          ) : (
            <>
              <button className={styles.currentPlanButton} type="button">
                Current Plan
              </button>
              <button
                className={styles.cancelButton}
                disabled={isMutatingPlan}
                onClick={(e) => {
                  e.stopPropagation()
                  onCancel?.()
                }}
                type="button"
              >
                {isMutatingPlan ? '…' : 'Cancel'}
              </button>
            </>
          )}
        </div>
      ) : (
        <button
          className={styles.ctaButton}
          disabled={isProcessing}
          onClick={(e) => {
            e.stopPropagation()
            onGetPlan?.()
          }}
          type="button"
        >
          {isPromoApplied ? 'Get Discounted Plan' : data.ctaText}
        </button>
      )}

      {data.addon && (
        <div className={styles.addonSection}>
          <h4 className={styles.addonTitle}>Add-Ons</h4>
          <div className={styles.addonItem}>
            <h5 className={styles.addonName}>
              {data.addon.name} - {data.addon.price}
            </h5>
            <p className={styles.addonDescription}>{data.addon.description}</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default SubscriptionCard
